import { useMemo, type ReactNode } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import {
  sportAnalyticTag,
  useSportContext,
} from '@sorare/core/src/contexts/sport';
import { useInSeasonLocalStorage } from '@sorare/core/src/hooks/search/useInSeasonLocalStorage';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { LIFECYCLE } from '@sorare/core/src/hooks/useLifecycle';
import { useQueryString } from '@sorare/core/src/hooks/useQueryString';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { useTour } from '@sorare/core/src/hooks/useTour';
import {
  SaleType,
  TOKENS_STACKED_LIMIT,
  createCardSorts,
} from '@sorare/core/src/lib/algolia';
import { TOGGLE_FILTERS } from '@sorare/core/src/lib/filters';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { ManagersSales as ManagersSalesOnboardingDialog } from '#src/components/market/OnboardingDialog/ManagersSales/index.tsx';
import { MarketRoot } from '#src/components/market/ui.tsx';
import { useDefaultFilters } from '#src/hooks/useDefaultFilters.ts';
import { secondaryMarketFilters as footballFilters } from '#src/searchCards/football/cardFilters.tsx';
import { AdvancedCardSearch } from '#src/searchCards/index.ts';
import { secondaryMarketFilters as mlbFilters } from '#src/searchCards/mlb/cardFilters.tsx';
import { secondaryMarketFilters as nbaFilters } from '#src/searchCards/nba/cardFilters.tsx';

const secondaryMarketFilters = {
  [Sport.FOOTBALL]: () => footballFilters,
  [Sport.BASEBALL]: mlbFilters,
  [Sport.NBA]: () => nbaFilters,
};
type Props = { banner: ReactNode };

export const ManagerSales = ({ banner }: Props) => {
  const {
    flags: { useMlbSeasonAverageScore = true },
  } = useFeatureFlags();
  const { sport } = useSportContext();

  const filtersTour = useTour(LIFECYCLE.transferMarket, 'filters');
  const cardsTour = useTour(LIFECYCLE.transferMarket, 'cards');
  useTitleAndDescription(
    metadatas.transfers.title,
    metadatas.transfers.description
  );
  const unstacked = useQueryString(SEARCH_PARAMS.UNSTACKED);
  const { algoliaCardIndexes } = useConfigContext();

  const displayStackedView = !unstacked;
  const [inSeason] = useInSeasonLocalStorage();

  const filters = useDefaultFilters({
    secondary: true,
  });

  const defaultSort = 'Cards On Sale Popular Player';

  const cardSortsWithBestValue = useMemo(
    () =>
      createCardSorts({
        indexPrefix: 'Cards On Sale',
        withBestValue: true,
        withHighestPrice: false,
        withHighestAverageScore: true,
        withPopularPlayer: true,
        withEndingSoon: false,
      }),
    []
  );

  const cardFilters = useMemo(
    () => secondaryMarketFilters[sport]?.(useMlbSeasonAverageScore),
    [sport, useMlbSeasonAverageScore]
  );

  return (
    <MarketRoot>
      {sport === Sport.FOOTBALL && <ManagersSalesOnboardingDialog />}
      <AdvancedCardSearch
        sport={sport}
        defaultHitsPerPage={40}
        defaultSort={defaultSort}
        sorts={cardSortsWithBestValue}
        defaultFilters={filters}
        cardFilters={cardFilters}
        analyticsTags={['TransferMarket', sportAnalyticTag[sport]]}
        removeEndedSingleSaleOffers
        initialIndexUIState={{
          ...(displayStackedView && {
            toggle: {
              [TOGGLE_FILTERS.inSeasonFilter.attribute]: inSeason,
            },
          }),
          sortBy: displayStackedView
            ? algoliaCardIndexes['Cards On Sale Popular Player']
            : algoliaCardIndexes['Cards On Sale Newly Listed'],
        }}
        distinct={displayStackedView ? TOKENS_STACKED_LIMIT : false}
        stackableTypes={[SaleType.SINGLE_SALE_OFFER]}
        attributesToRetrieve={[
          'asset_id',
          'sale',
          'player',
          'rarity',
          'season',
          'slug',
        ]}
        banner={banner}
        displayVideoTutorial
        {...(sport !== Sport.FOOTBALL && { filtersTour, cardsTour })}
      />
    </MarketRoot>
  );
};
